import HBIcon from "@/components/HBIcon";
import HBLoading from "@/components/HBLoading";
import { IMAGE_PREVIEW_SHORTCUT_GROUP_LIST } from "@/constants/shortcut";
import useShortcut from "@/utils/useShortcut";
import { useKeyPress, useLocalStorageState } from "ahooks";
import { Alert } from "antd";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { Else, If, Then } from "react-if";
import Shortcut from "../ShortcutModal/Shortcut";
import styles from "./index.module.less";

// const COLS = ["left", "right"];

const offsetEdge = 60;
const backgroundValues = ["black", "white", "grey", "grid", "slash"];
const zoomValue = [
  0.05, 0.1, 0.2, 0.33, 0.5, 0.75, 1, 1.25, 1.5, 2, 2.75, 3.75, 5, 7, 10,
];

export default function ImageViewer({
  src,
  preview = null,
  file,
  onClick = () => {},
}) {
  const containerRef = useRef(null);
  const readyDrag = useRef(false);
  const checkImageNatureWidthRef = useRef(null);

  const [loaded, setLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [draggable, setDraggable] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [rect, setRect] = useState({ top: 0, left: 0, width: 0, height: 0 });
  // const [viewCenterPoint, setViewCenterPoint] = useState([0, 0]); // 旋转中心点
  const [degree, setDegree] = useState(0); // 旋转角度
  const preDegree = useRef(degree);
  const mouseSource = useRef({ x: 0, y: 0 });
  const [background, setBackground] = useLocalStorageState("IMG_VIEWER_BG", {
    defaultValue: 0,
  });
  const [viewMode, setViewMode] = useState("scroll"); // 查看模式：scroll、zoom

  const commandStr = window.GLOBAL_INFO_OS === "macos" ? "⌘" : "ctrl";

  // 快捷键提醒（后续去掉）
  const [shortcutTip, setShortcutTip] = useLocalStorageState(
    "IMAGE_PREVIEW_NEW_SHORTCUT_TIP"
  );

  // 预览图
  const isPreview =
    (preview?.width && preview?.height && preview?.src) || false;

  // 缩放
  const zoomTo = useCallback(
    ({ zoom, x, y, align }) => {
      const currentTarget = containerRef.current;

      setRect((rect) => {
        if (align === "center") {
          return {
            left: (containerSize.width - zoom * naturalSize.width) / 2,
            top: (containerSize.height - zoom * naturalSize.height) / 2,
            width: zoom * naturalSize.width,
            height: zoom * naturalSize.height,
          };
        } else if (align === "top") {
          return {
            left: (containerSize.width - zoom * naturalSize.width) / 2,
            top: offsetEdge,
            width: zoom * naturalSize.width,
            height: zoom * naturalSize.height,
          };
        } else {
          if (x === undefined) {
            const left = Math.max(rect.left, 0);
            const right = Math.min(rect.left + rect.width, containerSize.width);
            x = (right + left) / 2;
          }
          if (y === undefined) {
            const top = Math.max(rect.top, 0);
            const bottom = Math.min(
              rect.top + rect.height,
              containerSize.height
            );
            y = (bottom + top) / 2;
          }

          const ratioX =
            (x - currentTarget.clientLeft - rect.left) / rect.width;
          const ratioY = (y - currentTarget.clientTop - rect.top) / rect.height;

          let offsetW = zoom * naturalSize.width - rect.width;
          offsetW = rect.width + offsetW <= 50 ? 50 - rect.width : offsetW; // 最小100px
          offsetW =
            (rect.width + offsetW) / naturalSize.width >= 10
              ? naturalSize.width * 10 - rect.width
              : offsetW; // 最大10倍放大

          const offsetH = offsetW / (rect.width / rect.height);

          return {
            left: rect.left - ratioX * offsetW,
            top: rect.top - ratioY * offsetH,
            width: rect.width + offsetW,
            height: rect.height + offsetH,
          };
        }
      });
    },
    [
      naturalSize.width,
      naturalSize.height,
      containerSize.width,
      containerSize.height,
    ]
  );

  // 滚动
  const scrollTo = useCallback(
    ({ x, y, absolute = false }) => {
      setRect((rect) => {
        let left = absolute ? x : rect.left + x;
        let top = absolute ? y : rect.top + y;

        left = Math.max(-rect.width + offsetEdge, left);
        left = Math.min(containerSize.width - offsetEdge, left);

        if (rect.height < containerSize.height - offsetEdge * 2) {
          top = Math.min(
            top,
            Math.max(rect.top, containerSize.height - rect.height - offsetEdge)
          );
          top = Math.max(top, Math.min(rect.top, offsetEdge));
        } else {
          top = Math.max(
            top,
            Math.min(rect.top, containerSize.height - rect.height - offsetEdge)
          );
          top = Math.min(top, Math.max(rect.top, offsetEdge));
        }

        if (rect.width < containerSize.width - offsetEdge * 2) {
          left = Math.min(
            left,
            Math.max(rect.left, containerSize.width - rect.width - offsetEdge)
          );
          left = Math.max(left, Math.min(rect.left, offsetEdge));
        } else {
          left = Math.max(
            left,
            Math.min(rect.left, containerSize.width - rect.width - offsetEdge)
          );
          left = Math.min(left, Math.max(rect.left, offsetEdge));
        }

        return {
          ...rect,
          left,
          top,
        };
      });
    },
    [containerSize.width, containerSize.height]
  );

  // 窗口变化自适应
  useEffect(() => {
    const resizeAction = () => {
      setContainerSize({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
    };

    resizeAction(); // 初始化

    window.addEventListener("resize", resizeAction);
    return () => {
      window.removeEventListener("resize", resizeAction);
    };
  }, []);

  // 切换为拖拽看图模式
  useEffect(() => {
    const keyDownAction = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.key === " ") {
        setDraggable(true);
      }
      const keyup = (e) => {
        if (
          e.key === "Meta" ||
          e.key === "Control" ||
          e.key === "Alt" ||
          e.key === " "
        ) {
          if (viewMode !== "zoom") {
            setDraggable(false);
          }
          document.removeEventListener("keyup", keyup);
        }
      };
      document.addEventListener("keyup", keyup);
    };
    document.addEventListener("keydown", keyDownAction);

    return () => {
      document.removeEventListener("keydown", keyDownAction);
    };
  }, [viewMode]);

  // 初始化设置 rect
  const initRect = useCallback(({ width, height, force = false }) => {
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    setRect((rect) => {
      if (rect.width !== 0 && rect.height !== 0 && !force) {
        // 预览图已经初始化过Rect，不需要重新设置
        return rect;
      }

      const result = {
        width: width,
        height: height,
      };

      if (result.width > containerWidth) {
        result.width = containerWidth - offsetEdge * 2;
        result.height = (result.width * height) / width;
      }

      result.left = (containerWidth - result.width) / 2;
      result.top = (containerHeight - result.height) / 2;

      if (result.height > containerHeight) {
        result.top = offsetEdge;
      }

      return result;
    });

    // 图片原始尺寸
    setNaturalSize({
      width: width,
      height: height,
    });
  }, []);

  // 预览图： 初始化利用预览图设置 rect
  useEffect(() => {
    if (isPreview) {
      initRect({
        width: preview.width,
        height: preview.height,
      });
    }
  }, [isPreview, initRect, preview]);

  // 图片加载完成
  useEffect(() => {
    setLoaded(false);
    setImageError(false);
    setDegree(0);

    const img = new Image();
    img.src = src;

    const cancelCheckImageNatureSize = () => {
      cancelAnimationFrame(checkImageNatureWidthRef.current);
    };

    const checkImageNatureSize = function () {
      if (img.naturalWidth && img.naturalWidth) {
        initRect({
          width: img.naturalWidth,
          height: img.naturalHeight,
          force: true,
        });
        cancelCheckImageNatureSize();
        return null;
      } else {
        return requestAnimationFrame(checkImageNatureSize);
      }
    };

    checkImageNatureWidthRef.current = checkImageNatureSize();

    img.onload = () => {
      if (!containerRef.current) {
        return false;
      } // 可能已经关闭组件

      cancelCheckImageNatureSize();
      setLoaded(true);
    };

    img.onerror = () => {
      // console.warn("查看大图加载失败：", JSON.stringify(file));
      setLoaded(true);
      setImageError(true);
      setDegree(0);

      cancelCheckImageNatureSize();
    };

    // 提前退出情况，取消检测
    return () => {
      cancelCheckImageNatureSize();
    };
  }, [src, initRect]);

  // 拖拽：mouseDown
  const onMouseDown = useCallback(
    (e) => {
      const isMiddleKey = e.button === 1;
      const preDraggable = draggable;

      if (isMiddleKey) {
        // Mouse middle key
        setDraggable(true);
      } else if (e.button !== 0 || draggable === false) {
        return false;
      }

      mouseSource.current = {
        left: rect.left,
        top: rect.top,
        mouseX: e.clientX,
        mouseY: e.clientY,
      };
      readyDrag.current = true;
      // 拖拽：mouseMove
      const mouseMove = (e) => {
        const source = mouseSource.current;
        let isDragging = dragging;
        if (
          readyDrag.current &&
          !dragging &&
          (Math.abs(source.mouseX - e.clientX) >= 5 ||
            Math.abs(source.mouseY - e.clientY) >= 5)
        ) {
          setDragging(true);
          isDragging = true;
        }

        if (isDragging) {
          setRect((rect) => {
            let left = source.left + (e.clientX - source.mouseX);
            left = Math.max(-rect.width + offsetEdge, left);
            left = Math.min(containerSize.width - offsetEdge, left);

            let top = source.top + (e.clientY - source.mouseY);
            top = Math.max(-rect.height + offsetEdge, top);
            top = Math.min(containerSize.height - offsetEdge, top);

            return {
              ...rect,
              left,
              top,
            };
          });
        }
      };

      // 拖拽：mouseUp
      const mouseUp = () => {
        if (isMiddleKey) {
          setDraggable(preDraggable);
        }
        if (readyDrag.current) {
          setDragging(false);
        }
        readyDrag.current = false;

        document.removeEventListener("mousemove", mouseMove);
        document.removeEventListener("mouseup", mouseUp);
      };

      document.addEventListener("mousemove", mouseMove);
      document.addEventListener("mouseup", mouseUp);
    },
    [rect, containerSize.width, containerSize.height, dragging, draggable]
  );

  // 回调Click事件
  const onMouseUp = useCallback(
    (e) => {
      if (!dragging) {
        onClick(e);
      }
    },
    [dragging, onClick]
  );

  // 切换自适应和1:1
  const toggleFit = useCallback(() => {
    if (Math.abs(naturalSize.width / rect.width - 1) < 0.01) {
      // 窗口适配居中展示
      const zoom = Math.min(
        (containerSize.width - offsetEdge * 2) / naturalSize.width,
        (containerSize.height - offsetEdge * 2) / naturalSize.height
      );

      zoomTo({ zoom, align: "center" });
    } else {
      // 1 : 1 展示
      const align =
        naturalSize.height >= containerSize.height - offsetEdge * 2
          ? "top"
          : "center";
      zoomTo({ zoom: 1, align });
    }
  }, [zoomTo, rect.width, containerSize, naturalSize]);

  // 单击图片
  const onImgClick = useCallback(
    (e) => {
      if (!e.currentTarget || dragging || e.button !== 0) {
        return false;
      }

      toggleFit({
        // x: e.clientX - currentTarget.clientLeft,
        // y: 100,
      });
    },
    [toggleFit, dragging]
  );

  // 滚轮
  const onWheel = useCallback(
    (e) => {
      if (!e.currentTarget) {
        return false;
      }
      const currentTarget = e.currentTarget;

      let mode = viewMode;
      if (e.ctrlKey || e.metaKey || e.altKey || draggable) {
        mode = "zoom";
      }

      if (mode === "scroll") {
        // 滚动模式
        const offsetW = -e.deltaX * 0.85;
        const offsetH = -e.deltaY * 0.85;

        scrollTo({ x: offsetW, y: offsetH });
      } else if (mode === "zoom") {
        // 缩放模式
        if (isNaN(e.deltaY)) {
          return false;
        }

        // 在图片上缩放
        const isInImg = e.target.nodeName === "IMG";

        let sensitive = e.deltaY * 0.0012 * (e.ctrlKey ? 10 : 1);

        zoomTo({
          zoom: (rect.width / naturalSize.width) * (1 - sensitive),
          x: (isInImg && e.clientX - currentTarget.clientLeft) || undefined,
          y: (isInImg && e.clientY - currentTarget.clientTop) || undefined,
        });
      }
    },
    [naturalSize.width, rect.width, scrollTo, zoomTo, viewMode, draggable]
  );

  // 旋转
  const rotate = useCallback((e) => {
    setDegree((degree) => {
      if (e && e.shiftKey) {
        degree += 90;
      } else {
        degree -= 90;
      }
      return degree;
    });
  }, []);

  // 角度变化
  useEffect(() => {
    const preDegreeValue = preDegree.current;
    if (preDegreeValue === degree) {
      return;
    } // 没有变化不处理
    preDegree.current = degree;

    // 重新设置位置
    setRect((rect) => {
      const left = Math.max(rect.left, 0);
      const right = Math.min(
        rect.left + rect.width,
        containerRef.current.clientWidth
      );
      const centerX = (right + left) / 2;

      const top = Math.max(rect.top, 0);
      const bottom = Math.min(
        rect.top + rect.height,
        containerRef.current.clientHeight
      );
      const centerY = (bottom + top) / 2;

      // 以可见区域的中心点旋转
      return {
        left: rect.top - centerY + centerX,
        top: centerX + centerY - rect.left - rect.width,
        width: rect.height,
        height: rect.width,
      };
    });

    // 交换宽高
    setNaturalSize(({ width, height }) => {
      return {
        width: height,
        height: width,
      };
    });
  }, [degree]);

  // useEffect(() => {
  //   const left = Math.max(rect.left, 0);
  //   const right = Math.min(rect.left + rect.width, containerRef.current.clientWidth);
  //   const centerX = (right + left) / 2;

  //   const top = Math.max(rect.top, 0);
  //   const bottom = Math.min(rect.top + rect.height, containerRef.current.clientHeight);
  //   const centerY = (bottom + top) / 2;

  //   setViewCenterPoint([centerX - rect.left, centerY - rect.top])
  // }, [rect])

  // 切换背景
  const switchBackground = useCallback(() => {
    setBackground((background) => {
      background += 1;
      if (background >= backgroundValues.length) {
        background = 0;
      }
      return background;
    });
  }, [setBackground]);

  // 切换查看模式
  const switchViewMode = useCallback(() => {
    const mode = viewMode === "scroll" ? "zoom" : "scroll";
    setViewMode(mode);
    setDraggable(mode === "zoom");
  }, [viewMode]);

  // 放大
  const zoomIn = useCallback(() => {
    const currentZoom = rect.width / naturalSize.width;
    const zoom = zoomValue.find((value) => value > currentZoom);
    zoomTo({ zoom: zoom || 10 });
  }, [rect.width, naturalSize.width, zoomTo]);

  // 缩小
  const zoomOut = useCallback(() => {
    const currentZoom = rect.width / naturalSize.width;
    const zoom = [...zoomValue].reverse().find((value) => value < currentZoom);
    zoomTo({ zoom: zoom || 0.05 });
  }, [rect.width, naturalSize.width, zoomTo]);

  // 快捷键：放大缩小[0, 1, =, -]
  useShortcut([187, 189, 48, 49], (e) => {
    e.preventDefault();

    if (e.key === "1") {
      // 自适应
      // 窗口适配居中展示
      const zoom = Math.min(
        (containerSize.width - offsetEdge * 2) / naturalSize.width,
        (containerSize.height - offsetEdge * 2) / naturalSize.height
      );

      zoomTo({
        zoom,
        align: "center",
      });
      return;
    }

    // let zoom = rect.width / naturalSize.width;
    if (e.key === "-") {
      zoomOut();
    } else if (e.key === "=") {
      zoomIn();
    } else if (e.key === "0") {
      zoomTo({ zoom: 1 });
    }
  });

  // 快捷键：旋转、背景、~切换模式~
  useShortcut(["t", "T", "b" /* 'v', 'space' */], (e) => {
    if (e.key === "t" || e.key === "T") {
      // 旋转
      rotate(e);
      return;
    }

    if (e.key === "b") {
      // 切换背景
      switchBackground();
    }

    // if (e.key === 'v') { // 切换查看模式
    //   switchViewMode();
    //   return;
    // }

    // if (e.key === ' ') { // 空格
    //   if (draggable) { return false; }

    //   const offsetY = (containerSize.height - offsetEdge * 2) * (e.shiftKey ? 1 : -1);
    //   scrollTo({ x: 0, y: offsetY });
    //   return;
    // }
  });

  // 快捷键：方向键
  useKeyPress(["uparrow", "downarrow"], (e) => {
    let offsetX = 0;
    let offsetY = 0;
    let variation = 30;
    if (e.shiftKey) {
      variation *= 10;
    }

    if (e.key === "ArrowUp") {
      offsetY = variation;
    } else if (e.key === "ArrowDown") {
      offsetY = -variation;
    }

    scrollTo({ x: offsetX, y: offsetY });
  });

  // 取消浏览器默认滚动事件行为
  useEffect(() => {
    const prevent = (e) => {
      e.preventDefault();
    };
    const ele = containerRef.current;

    ele.addEventListener("wheel", prevent, { capture: true, passive: false });

    return () => {
      ele.removeEventListener("wheel", prevent, {
        capture: true,
        passive: false,
      });
    };
  }, [loaded]);

  // 快捷键显示提示：第一次出现设置显示，执行CSS动画，后续移除代码
  useEffect(() => {
    if (shortcutTip === undefined) {
      setTimeout(() => {
        setShortcutTip(true);
      }, 1000);
    }
  }, [shortcutTip, setShortcutTip]);

  const imageStyle = {
    width: `${degree % 180 === 0 ? rect.width : rect.height}px`,
    height: `${degree % 180 === 0 ? rect.height : rect.width}px`,
    transform: `rotate(${degree}deg)`,
    // transformOrigin: `${viewCenterPoint[0]}px ${viewCenterPoint[1]}px`
  };

  return (
    <div
      id="imageViewerWrapper"
      className={classNames([
        styles.imageViewer,
        styles[`background_${backgroundValues[background || 0]}`],
        dragging ? styles.dragging : null,
      ])}
      ref={containerRef}
      draggable={false}
      onMouseDown={onMouseDown}
      onMouseUpCapture={onMouseUp}
      onWheelCapture={onWheel}
      onClick={() => {
        setShortcutTip(false);
      }}
    >
      <If condition={!imageError}>
        <Then>
          <div
            className={classNames([
              styles.imageWrapper,
              draggable ? styles.draggable : null,
              rect.width >= naturalSize.width ? styles.zoomOut : styles.zoomIn,
            ])}
            onMouseUpCapture={onImgClick}
            style={{
              transform: `translate(${rect.left}px, ${rect.top}px)`,
              width: `${rect.width}px`,
              height: `${rect.height}px`,
            }}
          >
            <img
              className={styles.imageEle}
              src={src}
              alt="查看图片"
              draggable={!draggable}
              onMouseDown={(e) => {
                draggable && e.preventDefault();
              }} // 解决firefox下拖拽图片问题
              style={imageStyle}
            />
            {isPreview && !loaded && (
              <img
                className={styles.imageElePreview}
                src={preview.src}
                alt="预览图片"
                draggable={!draggable}
                onMouseDown={(e) => {
                  draggable && e.preventDefault();
                }} // 解决firefox下拖拽图片问题
                style={imageStyle}
              />
            )}
          </div>

          {!loaded && (
            <span className={styles.loading}>
              <HBLoading size={"30px"} />
            </span>
          )}

          <ul className={styles.tools}>
            <li onClick={switchBackground} data-tooltip="切换背景 [B]">
              <HBIcon type="ic_pic_bg" />
            </li>
            <li className={styles.divider}></li>
            <li onClick={rotate} data-tooltip="旋转 [T]">
              <HBIcon type="ic_rotate" />
            </li>
            <li onClick={toggleFit} data-tooltip="适应窗口 [1]">
              <HBIcon type="ic_fit" />
            </li>
            <li
              onClick={switchViewMode}
              data-tooltip="拖拽+缩放 [长按空格]"
              className={classNames({ active: draggable })}
            >
              <HBIcon type="ic_pic_hand" />
            </li>
            <li onClick={zoomIn} data-tooltip={`放大 [${commandStr} + 滚轮]`}>
              <HBIcon type="ic_enlarge" />
            </li>

            <li onClick={zoomOut} data-tooltip={`缩小 [${commandStr} + 滚轮]`}>
              <HBIcon type="ic_reduce" />
            </li>

            <li
              className={styles.zoomValue}
              data-tooltip="实际大小 [0]"
              onClick={toggleFit}
            >
              {Math.round((rect.width / naturalSize.width) * 100)}%
            </li>
          </ul>

          <span className={styles.imageSize} data-tooltip="图片信息">
            {naturalSize.width} x {naturalSize.height}
            {file?.type ? " | " + file.type.split("/")[1].toUpperCase() : ""}
          </span>
        </Then>
        <Else>
          <If condition={imageError}>
            <Then>
              <Alert
                className={styles.loadError}
                message="图片加载失败"
                description={
                  <a href={src} target="_blank" rel="noreferrer">
                    {src}
                  </a>
                }
                type="error"
                showIcon
              />
            </Then>
          </If>
        </Else>
      </If>

      <div
        className={classNames([
          styles.shortcut,
          { [styles.show]: shortcutTip },
        ])}
      >
        <HBIcon type="keyboard" />
        <div className={styles.details}>
          <div className={styles.title}>快捷键说明</div>
          <Shortcut data={IMAGE_PREVIEW_SHORTCUT_GROUP_LIST} />
        </div>
      </div>
    </div>
  );
}
